import React, { useMemo } from 'react';
import styled from 'styled-components';
import Slider from 'react-slick';
import { useTheme } from '@material-ui/core/styles';

import TwoWeekBanner from '../../../../../images/banners/bnr_two_week.jpg';
import WillerBanner from '../../../../../images/banners/bnr_willer.jpg';
import ImabariBanner from '../../../../../images/banners/bnr_imabari.png';
import BetsukaiBanner from '../../../../../images/banners/bnr_betsukai.png';
import TobuBanner from '../../../../../images/banners/bnr_tobu.png';
import IslandBanner from '../../../../../images/banners/bnr_island.png';
import TobaBanner from '../../../../../images/banners/bnr_toba.png';
import LineBanner from '../../../../../images/banners/bnr_line.png';
import JRBanner from '../../../../../images/banners/bnr_jr.png';
import SaiyoritsuUpBanner from '../../../../../images/banners/bnr_saiyoritsu_up.jpg';
// 月別バナー画像をインポート（必要な月のみ）
import JanuaryBanner from '../../../../../images/banners/months/bnr_1.jpg';
import FebruaryBanner from '../../../../../images/banners/months/bnr_2.jpg';
import MarchBanner from '../../../../../images/banners/months/bnr_3.jpg';
import AprilBanner from '../../../../../images/banners/months/bnr_4.jpg';
import MayBanner from '../../../../../images/banners/months/bnr_5.jpg';
import JuneBanner from '../../../../../images/banners/months/bnr_6.jpg';
import JulyMonthBanner from '../../../../../images/banners/months/bnr_7.jpg';
import AugustMonthBanner from '../../../../../images/banners/months/bnr_8.jpg';
import SeptemberMonthBanner from '../../../../../images/banners/months/bnr_9.jpg';
import OctoberBanner from '../../../../../images/banners/months/bnr_10.jpg';
import NovemberBanner from '../../../../../images/banners/months/bnr_11.jpg';
import DecemberBanner from '../../../../../images/banners/months/bnr_12.jpg';
import LazyLoadComponent from '~/src/core/components/LazyLoad/LazyLoadComponent';
import Banner from '../molecules/Banner';
import {
  MCPR_IMABARI,
  MCPR_BETSUKAI,
  MCPR_KAGOSHIMA_ISLAND_COMPANY,
  MCPR_TOBA,
} from '../../../routing';
import { DATA_GTM_EVENT_NAME } from '../../../constants/gtm';
import moment from 'moment';

// 月別バナー画像のマッピング
const MONTH_BANNERS = {
  1: JanuaryBanner,
  2: FebruaryBanner,
  3: MarchBanner,
  4: AprilBanner,
  5: MayBanner,
  6: JuneBanner,
  7: JulyMonthBanner,
  8: AugustMonthBanner,
  9: SeptemberMonthBanner,
  10: OctoberBanner,
  11: NovemberBanner,
  12: DecemberBanner,
};

// 月に応じてバナー画像を取得する関数
const getMonthBanner = (month: number): string => {
  return MONTH_BANNERS[month] || JanuaryBanner; // デフォルトは1月の画像
};

const settings = (theme) => ({
  slidesToShow: 3,
  slidesToScroll: 1,
  autoplay: true,
  speed: 600,
  autoplaySpeed: 2500,
  centerMode: true,
  arrows: false,
  dots: false,
  centerPadding: '84px',
  responsive: [
    {
      breakpoint: theme.breakpoints.values.sm,
      settings: {
        slidesToShow: 1,
        centerPadding: '29px',
      },
    },
    {
      breakpoint: theme.breakpoints.values.md,
      settings: {
        slidesToShow: 2,
        centerPadding: '84px',
      },
    },
  ],
});

const Banners = () => {
  const theme = useTheme();
  const sliderProps = useMemo(() => settings(theme), [theme]);
  return (
    <Wrapper>
      <Slider {...sliderProps}>
        {BANNER_IMAGES.map(({ href, src, alt, linkProps }) => (
          <LazyLoadComponent key={href}>
            <BannerWrapper key={href}>
              <Banner href={href} src={src} alt={alt} linkProps={linkProps} />
            </BannerWrapper>
          </LazyLoadComponent>
        ))}
      </Slider>
    </Wrapper>
  );
};

export default Banners;

const startMonth =
  moment().endOf('month').diff(moment(), 'days') > 7
    ? moment()
    : moment().add(1, 'month');

const BANNER_IMAGES: Array<{
  href: string;
  src: string;
  alt: string;
  linkProps?: {
    target?: string;
    rel?: string;
    [DATA_GTM_EVENT_NAME]: string;
  };
}> = [
  {
    href: '/lp/jreast-otona-kyujitsu',
    src: JRBanner,
    alt: '大人の休日倶楽部特集',
    linkProps: {
      [DATA_GTM_EVENT_NAME]: '2026年大人の休日倶楽部バナー',
    },
  },
  {
    href: '/plans?chance=true&utm_source=user+applications&utm_medium=mv&utm_campaign=no+application+pickup+mv',
    src: SaiyoritsuUpBanner,
    alt: '今なら採用率UP!?特集',
    linkProps: {
      [DATA_GTM_EVENT_NAME]: '今なら採用率UP!?特集バナー',
    },
  },
  {
    href: '/plans?term=14',
    src: TwoWeekBanner,
    alt: '2週間以内で参加できるおてつたび',
    linkProps: {
      [DATA_GTM_EVENT_NAME]: '2024年2週間バナー',
    },
  },
  {
    href: process.env.LINE_BNR_OFFICIAL_DIRECT_URL || '',
    src: LineBanner,
    alt: 'LINEであなたにぴったりのおてつたびをご紹介！',
    linkProps: {
      target: '_blank',
      rel: 'noopener noreferrer',
      [DATA_GTM_EVENT_NAME]: '2025年Lineバナー',
    },
  },
  // 3ヶ月分のバナーを表示
  ...Array(3)
    .fill(0)
    .map((_, index) => {
      const currentMonth = startMonth.clone().add(index, 'month');
      const beginningOfMonth = currentMonth.clone().startOf('month');
      const endOfMonth = currentMonth.clone().endOf('month');
      return {
        href: `/plans?endDate=${endOfMonth.format(
          'YYYY-MM-DD',
        )}&startDate=${beginningOfMonth.format('YYYY-MM-DD')}`,
        src: getMonthBanner(currentMonth.month() + 1), // 月に応じて画像を取得
        alt: `${currentMonth.format('YYYY年M月')}に参加できるおてつたび`,
        linkProps: {
          [DATA_GTM_EVENT_NAME]: `${currentMonth.format('YYYY年M月')}バナー`,
        },
      };
    }),
  {
    href: MCPR_TOBA,
    src: TobaBanner,
    alt: 'おてつたび x 鳥羽市',
    linkProps: {
      [DATA_GTM_EVENT_NAME]: '2025年鳥羽LPバナークリック',
    },
  },
  {
    href: MCPR_IMABARI,
    src: ImabariBanner,
    alt: 'おてつたび x 愛媛県今治市',
    linkProps: {
      [DATA_GTM_EVENT_NAME]: '2024年今治市LPバナークリック',
    },
  },
  {
    href: MCPR_BETSUKAI,
    src: BetsukaiBanner,
    alt: 'おてつたび x 別海町',
    linkProps: {
      [DATA_GTM_EVENT_NAME]: '2024年別海町LPバナークリック',
    },
  },
  {
    href: MCPR_KAGOSHIMA_ISLAND_COMPANY,
    src: IslandBanner,
    alt: 'おてつたび × island company.',
    linkProps: {
      [DATA_GTM_EVENT_NAME]: '2025年島と旅LPバナークリック',
    },
  },
  {
    href: 'https://travel.willer.co.jp/campaign/otetsutabi/?utm_source=otetsutabi-top&utm_medium=referral&mid=1808',
    src: WillerBanner,
    alt: 'WILLER EXPRESS',
    linkProps: {
      target: '_blank',
      rel: 'noopener noreferrer',
      [DATA_GTM_EVENT_NAME]: '2024年willerバナー',
    },
  },
  {
    href: 'https://www.tobu.co.jp/odekake/campaign/otetsutabi_collab/',
    src: TobuBanner,
    alt: '東武鉄道',
    linkProps: {
      target: '_blank',
      rel: 'noopener noreferrer',
      [DATA_GTM_EVENT_NAME]: '2024年東武鉄道バナー',
    },
  },
];

const Wrapper = styled.div`
  width: 100%;
  margin: ${({ theme }) => theme.spacing(0, 'auto', 2)};
`;
const BannerWrapper = styled.div`
  max-width: 100%;
  padding: ${({ theme }) => theme.spacing(0, 1)};
`;
